'use client';

import { createContext, useContext, FC, PropsWithChildren } from 'react';

/**
 * Flags evaluated server-side (via launchdarkly-node-client-sdk) against an
 * anonymous context. These are available immediately on first render — before
 * the client-side LD SDK initializes or any user auth state is resolved.
 *
 * Only add flags here that either:
 *   1. Are static / not user-context-dependent
 *   2. Need to be available very early in the app lifecycle
 *   3. Need to be available for unauthenticated users
 *
 * For all other flags, continue using useFlagsProxy() which reads from the
 * client-side LD SDK after user identification.
 */
export type ServerFlags = {
  cloudAuthNewLoginExperience: boolean;
};

export const DEFAULT_SERVER_FLAGS: ServerFlags = {
  cloudAuthNewLoginExperience: false,
};

const ServerFlagsContext = createContext<ServerFlags>(DEFAULT_SERVER_FLAGS);

export const ServerFlagsProvider: FC<PropsWithChildren<{ flags: ServerFlags }>> = ({ flags, children }) => (
  <ServerFlagsContext.Provider value={flags}>
    {children}
  </ServerFlagsContext.Provider>
);

/**
 * Returns feature flags that were evaluated on the server at render time.
 * These are safe to read in any client component.
 * @note Only a subset of flags are available immediately on first render.
 * If the flag depends on the user auth state, then you need to
 * use useFlagsProxy() to read the flag after the user is authenticated.
 */
export const useServerFlags = (): ServerFlags => useContext(ServerFlagsContext);
